import React, { memo, forwardRef, useCallback, useMemo, useRef, useContext } from 'react';
import classnames from 'classnames';
import { createUseStyles, Select } from '@Sage/hoi-poi-ui';
import { getList } from 'lib/services/valueLists';
import { useWeb5Context } from 'lib/Web5Context';
import { MATCHING_VALUES } from '../../constants';

import ImportContext from './../../ImportContext';

// Select component doesn't handle negative integers, and backend need them. So...
// All the values that come from props (data, options, matchOptions...) should come as Integers
// All the values and options that are passed to the Select component should be Strings
import { transformToString, transformToInteger } from '../utils/transformValue';

import styles from './styles';
const useStyles = createUseStyles(styles, 'FieldFormatter');

const FuzzyFieldFormatter = (
    { data, className, options, matchOptions, onChange, fieldTable, node, api },
    ref,
) => {
    const classes = useStyles();
    const fieldClassName = classnames(className, classes.field);
    const { t, language: lang } = useWeb5Context();
    const { language: externalLanguage } = useContext(ImportContext);
    const language = externalLanguage || lang;

    const selectRef = useRef();

    const isCreate = useMemo(() => data.nameField === MATCHING_VALUES.creatAsNew, [data]);

    const value = useMemo(() => {
        if ((!data.nameField && data.nameField !== 0) || data.nameField === MATCHING_VALUES.noValue)
            return null;

        if (data.nameField === MATCHING_VALUES.creatAsNew) {
            return {
                label: t('cfm_label_create_as_new_one'),
                value: transformToString(MATCHING_VALUES.creatAsNew),
            };
        }

        const optionExists = options.find(
            (option) => transformToString(option.value) === transformToString(data.nameField),
        );

        if (!optionExists && data.name && data.nameField) {
            options.push({ label: node.data?.label, value: transformToString(data?.nameField) });
            return {
                label: node.data?.label,
                value: transformToString(data?.nameField),
            };
        }

        return optionExists;
    }, [data.nameField, data.name, t, options, node.data?.label]);

    const finalOptions = useCallback(() => {
        return options.map((current) => ({
            label: current.label,
            value: transformToString(current.value),
        }));
    }, [options]);

    const onLoadOptions = useCallback(
        (searchText) => {
            return getList({ list: fieldTable, language, searchText })
                .then(({ data }) => {
                    let finalOptions = data.map((item) => {
                        return {
                            label: item.description,
                            value: transformToString(item.id),
                        };
                    });

                    return finalOptions;
                })
                .catch(console.error);
        },
        [fieldTable, language],
    );

    const onChangeSelect = useCallback(
        (option) => {
            onChange(
                data.index,
                option ? transformToInteger(option.value) : MATCHING_VALUES.noValue,
            );

            node.setData({
                name: data.name,
                nameField: transformToInteger(option?.value) || MATCHING_VALUES.noValue,
                label: option?.label,
            });
            setTimeout(() => api.refreshCells({ columns: ['nameField'], force: true }), 100);
        },
        [api, data, node, onChange],
    );

    const rsOverrides = useMemo(() => (isCreate ? { menuIsOpen: false } : {}), [isCreate]);

    const overrides = useMemo(
        () => ({
            control: {
                style: {
                    background: 'none',
                    border: 'none',
                    '&:hover': {
                        cursor: isCreate ? 'initial' : 'pointer',
                    },
                },
            },
            controlFocused: { style: { background: 'none', border: 'none' } },
            clearIndicator: { style: { cursor: 'pointer' } },
            'react-select': { ...rsOverrides },
        }),
        [isCreate, rsOverrides],
    );

    return (
        <div ref={ref} className={fieldClassName}>
            <Select
                getRef={(ref) => {
                    selectRef.current = ref;
                }}
                className={classes.select}
                placeholder={t('cfm_placeholder_select_one_match')}
                options={finalOptions}
                onChange={onChangeSelect}
                value={value}
                isFullWidth
                isFuzzy={!isCreate}
                loadOptions={matchOptions ? onLoadOptions : null}
                hideDropdownIndicator
                isSearchable={!isCreate}
                isReadOnly={value?.value === transformToString(MATCHING_VALUES.creatAsNew)}
                overrides={{
                    ...overrides,
                }}
            />
        </div>
    );
};

export default memo(forwardRef(FuzzyFieldFormatter));
